"use client";

import cnBind from "classnames/bind";
import { useEffect, useRef, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import { INDUSTRY_PRESET_ORDER, INDUSTRY_PRESETS } from "@/data/industryPresets";
import {
    freePlacesBadge,
    isCampaignOfferActive,
    isCampaignVisible,
    type PublicCampaign,
} from "@/shared/campaign/beautyCampaign";

import { useIndustryPreset } from "./IndustryPresetContext";

import styles from "./IndustryPresetBar.module.scss";

const cx = cnBind.bind(styles);

/** Fixed bottom control — Главная (УТП продукта) ↔ отрасли (УТП пресетов). */
export const IndustryPresetBar = ({ campaign }: { campaign?: PublicCampaign | null }) => {
    const { t } = useTranslation("common");
    const { activePreset, preset, isPlatform, selectPlatform, setActivePreset } = useIndustryPreset();
    const tabsRef = useRef<HTMLDivElement>(null);
    const offer = isCampaignOfferActive(campaign);
    const tease = isCampaignVisible(campaign);

    useEffect(() => {
        const root = tabsRef.current;
        if (!root) return;
        const sel = isPlatform ? '[data-preset="platform"]' : `[data-preset="${activePreset}"]`;
        root.querySelector<HTMLButtonElement>(sel)?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
        });
    }, [activePreset, isPlatform]);

    return (
        <div
            className={cx("bar")}
            style={
                {
                    ["--preset-accent" as string]: isPlatform ? "#229ed9" : preset.accent,
                    ["--preset-accent-soft" as string]: isPlatform
                        ? "rgba(34, 158, 217, 0.18)"
                        : preset.accentSoft,
                } as CSSProperties
            }
        >
            <div className={cx("scopeHint")} aria-hidden="true">
                <span className={cx({ scopeOn: isPlatform })}>{t("home.presetBar.scopeProduct")}</span>
                <span className={cx("scopeSep")}>/</span>
                <span className={cx({ scopeOn: !isPlatform })}>{t("home.presetBar.scopeIndustry")}</span>
            </div>
            <div
                ref={tabsRef}
                className={cx("tabs")}
                role="tablist"
                aria-label={t("home.presetBar.tabsAriaLabel")}
            >
                <button
                    type="button"
                    role="tab"
                    aria-selected={isPlatform}
                    data-preset="platform"
                    className={cx("tab", "tabPlatform", { tabActive: isPlatform })}
                    onClick={selectPlatform}
                >
                    <span className={cx("tabLabel")}>{t("home.presetBar.homeTab")}</span>
                    <span className={cx("tabBadge")}>TEZAM</span>
                </button>

                {INDUSTRY_PRESET_ORDER.map((key) => {
                    const item = INDUSTRY_PRESETS[key];
                    const isActive = !isPlatform && key === activePreset;
                    const label = t(`home.presetBar.industries.${key}`, { defaultValue: item.label });

                    return (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            data-preset={key}
                            className={cx("tab", { tabActive: isActive })}
                            style={
                                isActive
                                    ? ({
                                          ["--tab-accent" as string]: item.accent,
                                          ["--tab-accent-soft" as string]: item.accentSoft,
                                      } as CSSProperties)
                                    : undefined
                            }
                            onClick={() => setActivePreset(key)}
                        >
                            <span className={cx("tabLabel")}>{label}</span>
                            {key === "beauty" && offer && campaign ? (
                                <span className={cx("tabBadge")}>
                                    {freePlacesBadge(campaign.remaining, campaign.maxActivations)}
                                </span>
                            ) : key === "beauty" && tease && campaign?.status === "SCHEDULED" ? (
                                <span className={cx("tabBadge")}>{t("home.presetBar.places100")}</span>
                            ) : null}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
